import { Center, Loader, Portal } from '@mantine/core';
import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { CHANGE_PASSWORD, LOGIN } from '@/shared/config/routes';
import { useIsExpired } from '@/entities/user/session';
import { useDetailsState } from '@/entities/srm/enrichment';

const EnrichmentDetails = lazy(() =>
  import('@/features/enrichment-details').then((module) => ({
    default: module.EnrichmentDetails,
  })),
);
const LoginDialog = lazy(() =>
  import('@/app/ui/login-dialog').then((module) => ({
    default: module.LoginDialog,
  })),
);

function DeferredDialog({
  opened,
  children,
}: {
  opened: boolean;
  children: ReactNode;
}) {
  const [activated, setActivated] = useState(opened);

  useEffect(() => {
    if (opened) setActivated(true);
  }, [opened]);

  // Keep dialog state and exit transitions after the first opening.
  if (!opened && !activated) return null;

  return (
    <Suspense
      fallback={
        opened ? (
          <Portal>
            <Center
              pos='fixed'
              inset={0}
              style={{ pointerEvents: 'none', zIndex: 1001 }}
            >
              <Loader data-testid='dialog-chunk-loader' />
            </Center>
          </Portal>
        ) : null
      }
    >
      {children}
    </Suspense>
  );
}

export function GlobalDialogs() {
  const [rootTaskId] = useDetailsState({
    selector: (state) => state.root_task_id,
  });
  const isExpired = useIsExpired();
  const { pathname } = useLocation();
  const loginOpened =
    isExpired &&
    !pathname.startsWith(LOGIN) &&
    !pathname.startsWith(CHANGE_PASSWORD);

  return (
    <>
      <DeferredDialog opened={Boolean(rootTaskId)}>
        <EnrichmentDetails />
      </DeferredDialog>
      <DeferredDialog opened={loginOpened}>
        <LoginDialog opened={loginOpened} />
      </DeferredDialog>
    </>
  );
}
